import { Link } from 'react-router-dom';
import { ArrowLeft, type LucideIcon } from 'lucide-react';

export type PageType = 'dashboard' | 'audits' | 'audit' | 'clients' | 'client' | 'proposal' | 'storage' | 'new-audit' | 'projects' | 'project' | 'settings' | 'outreach';

const CONFIG: Record<PageType, { color: string; bg: string; border: string; badge: string }> = {
  dashboard:  { color: 'text-teal-700',   bg: 'bg-teal-50',    border: 'border-teal-200', badge: 'Dashboard' },
  audits:     { color: 'text-indigo-700',  bg: 'bg-indigo-50',  border: 'border-indigo-200', badge: 'Auditorías' },
  audit:      { color: 'text-indigo-700',  bg: 'bg-indigo-50',  border: 'border-indigo-200', badge: 'Auditoría' },
  'new-audit':{ color: 'text-indigo-700',  bg: 'bg-indigo-50',  border: 'border-indigo-200', badge: 'Nueva Auditoría' },
  clients:    { color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200', badge: 'Clientes' },
  client:     { color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200', badge: 'Cliente' },
  proposal:   { color: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-200', badge: 'Propuesta' },
  storage:    { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200', badge: 'Almacenamiento' },
  projects:   { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'Proyectos' },
  project:    { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'Proyecto' },
  settings:   { color: 'text-gray-700',    bg: 'bg-gray-50',    border: 'border-gray-200', badge: 'Ajustes' },
  outreach:   { color: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200', badge: 'Outreach' },
};

interface PageHeaderProps {
  type: PageType;
  icon: LucideIcon;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  backLink?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

export default function PageHeader({ type, icon: Icon, title, subtitle, backLink, actions, children }: PageHeaderProps) {
  const cfg = CONFIG[type];

  return (
    <div className={`-mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8 mb-6 px-4 md:px-6 lg:px-8 pt-5 pb-4 ${cfg.bg} border-b ${cfg.border}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {backLink && (
            <Link to={backLink} className="p-2 rounded-lg hover:bg-white/60 text-gray-400 shrink-0">
              <ArrowLeft size={18} />
            </Link>
          )}
          <div className={`shrink-0 w-10 h-10 rounded-xl ${cfg.bg} border ${cfg.border} flex items-center justify-center`}>
            <Icon size={20} className={cfg.color} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-widest ${cfg.color} px-1.5 py-0.5 rounded ${cfg.bg} border ${cfg.border}`}>
                {cfg.badge}
              </span>
            </div>
            <h1 className="text-xl font-bold text-gray-900 truncate mt-0.5">{title}</h1>
            {subtitle && <div className="text-sm text-gray-500 mt-0.5">{subtitle}</div>}
            {children}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}
